import {getTestProps} from '@benchling/js-test-utils';
import {css} from 'aphrodite';
import React from 'react';

import Label from 'ui-library/bs/Label';
import {BLUE_DARKER, BLUE_LIGHTEST} from 'ui-library/Colors';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';

import {agentProgressStyles} from './ChatProgress';

export type ThinkingHeaderVariant = 'large' | 'compact';

export interface ThinkingHeaderProps {
  variant: ThinkingHeaderVariant;
  headerLabel?: string;
  stepCountText?: string;
  testComponent?: string;
}

export interface ThinkingSpinnerProps {
  text?: string;
  testComponent?: string;
}

/** Blue duotone spinner used in thinking progress UI (header and active tool rows). */
export function ThinkingDuotoneSpinner(): JSX.Element {
  return (
    <BenchlingDuotoneIcon
      icon="spinner-third"
      primaryColor={BLUE_DARKER}
      secondaryColor={BLUE_LIGHTEST}
      spin={true}
    />
  );
}

/**
 * Unified "Thinking" header that animates between large (pre-tool) and compact (tool list / completed)
 * variants while keeping the same label element mounted.
 */
export function ThinkingHeader({
  variant,
  headerLabel = 'Thinking',
  stepCountText = '',
  testComponent,
}: ThinkingHeaderProps): JSX.Element {
  const isLarge = variant === 'large';

  return (
    <div
      className={css(agentProgressStyles.thinkingHeader)}
      {...(testComponent && isLarge
        ? getTestProps({
            component: testComponent,
            element: 'thinkingSection',
          })
        : {})}
    >
      <Label
        className={css(
          agentProgressStyles.thinkingLabel,
          isLarge ? agentProgressStyles.thinkingLabelLarge : agentProgressStyles.thinkingLabelCompact
        )}
      >
        {headerLabel}
        {stepCountText}
      </Label>
    </div>
  );
}

/**
 * @deprecated Prefer ThinkingHeader with variant="large".
 */
export function ThinkingSpinner({text = 'Thinking', testComponent}: ThinkingSpinnerProps): JSX.Element {
  const stepCountText = text.startsWith('Thinking') ? text.slice('Thinking'.length) : text;
  return (
    <div className={css(agentProgressStyles.thinkingSection)}>
      <ThinkingDuotoneSpinner />
      <ThinkingHeader variant="large" stepCountText={stepCountText} testComponent={testComponent} />
    </div>
  );
}
